import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ShieldCheck, History } from 'lucide-react'
import clsx from 'clsx'
import { tpmApi, deviceStatusLabel, deviceStatusColor } from '../../services/tpm'

export default function TpmDeviceDetail() {
  const { deviceId } = useParams()

  const { data: device, isLoading } = useQuery({
    queryKey: ['tpm', 'device', deviceId],
    queryFn: () => tpmApi.getDevice(deviceId),
  })

  const { data: history = [] } = useQuery({
    queryKey: ['tpm', 'device', deviceId, 'history'],
    queryFn: () => tpmApi.getDeviceHistory(deviceId),
  })

  if (isLoading) return <div className="text-gray-400 text-sm">Carregando...</div>
  if (!device) return <div className="text-red-600 text-sm">Device não encontrado.</div>

  return (
    <div className="space-y-4">
      <Link to="/tpm" className="inline-flex items-center text-sm text-gray-500 hover:text-theme-primary">
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar para devices
      </Link>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-theme-primary" />
              {device.label || device.id.slice(0, 8)}
            </h1>
            <div className="text-xs text-gray-500 mt-1 font-mono">{device.id}</div>
          </div>
          <span className={clsx('px-3 py-1 rounded text-sm font-medium', deviceStatusColor(device.status))}>
            {deviceStatusLabel(device.status)}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-sm">
          <Field label="EK fingerprint (sha256)" value={device.ekPubSha256} mono />
          <Field label="Vault UUID" value={device.vaultUuid || '—'} mono />
          <Field label="Tenant" value={device.tenantId || '—'} mono />
          <Field label="VPN IP" value={device.lastVpnIp || '—'} mono />
          <Field label="PCR baseline hash" value={device.lastPcrBaselineHash || '—'} mono />
          <Field label="Recovery blob" value={device.hasRecoveryBlob ? 'sim' : 'não'} />
          <Field label="Criado" value={fmt(device.createdAt)} />
          <Field label="Atualizado" value={fmt(device.updatedAt)} />
          <Field label="Visto pela ultima vez" value={fmt(device.lastSeenAt)} />
          <Field label="Enrolled em" value={fmt(device.enrolledAt)} />
          {device.revokedAt && <Field label="Revogado em" value={fmt(device.revokedAt)} />}
          {device.revokedReason && <Field label="Motivo da revogação" value={device.revokedReason} />}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
        <h2 className="text-md font-semibold flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-gray-500" />
          Histórico de transições
        </h2>
        {history.length === 0 && <div className="text-sm text-gray-400">Sem transições registradas.</div>}
        <div className="space-y-2">
          {history.map((h) => (
            <div key={h.id} className="text-sm border-l-2 border-theme-primary/30 pl-3 py-1">
              <div>
                <span className="font-medium">{h.fromStatus}</span>
                {' → '}
                <span className="font-medium">{h.toStatus}</span>
                {' '}<span className="text-gray-500">({h.reason})</span>
              </div>
              <div className="text-xs text-gray-500">
                {fmt(h.createdAt)} • user: {h.userId || 'system'}
              </div>
              {h.contextJson && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-400">contexto</summary>
                  <pre className="bg-gray-50 p-2 rounded mt-1 overflow-x-auto">{h.contextJson}</pre>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs text-gray-500 uppercase">{label}</div>
      <div className={clsx('text-gray-800 break-all', mono && 'font-mono text-xs')}>{value}</div>
    </div>
  )
}

function fmt(d) {
  if (!d) return '—'
  try { return new Date(d).toLocaleString() } catch { return d }
}
